import { useMemo, useState } from 'react';
import axios from '../lib/axios';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '../lib/cn';

export default function StoreAdminSignupPage() {
  const [form, setForm] = useState({ store_code: '', username: '', pw: '', pw2: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const canSubmit = useMemo(() => (
    form.store_code.trim().length > 0 &&
    form.username.trim().length > 0 &&
    (form.pw || '').length >= 4 &&
    form.pw === form.pw2
  ), [form.store_code, form.username, form.pw, form.pw2]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    setError('');
    setLoading(true);
    try {
      const payload = {
        store_code: form.store_code.trim(),
        username: form.username.trim(),
        pw: form.pw,
      };
      await axios.post('/store-admin/signup', payload);
      navigate('/store-admin/pending', {
        state: { username: payload.username, store_code: payload.store_code },
      });
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'store_not_found') setError('매장코드를 찾을 수 없어. 코드 다시 확인해줘.');
      else if (code === 'store_inactive') setError('비활성(숨김) 매장이야. 상위관리자에게 문의해줘.');
      else if (code === 'username_exists') setError('이미 사용중인 아이디야. 다른 아이디로 해줘.');
      else if (code === 'username_length') setError('아이디 길이는 3~40자로 해줘.');
      else if (code === 'pw_too_short') setError('비밀번호는 4자 이상으로 해줘.');
      else if (code === 'store_code_required') setError('매장코드를 입력해줘.');
      else if (code === 'username_required') setError('아이디를 입력해줘.');
      else if (code === 'pw_required') setError('비밀번호를 입력해줘.');
      else setError('가입 요청 실패. 다시 시도해줘.');
    } finally {
      setLoading(false);
    }
  };

  const pwMismatch = (form.pw2 || '').length > 0 && form.pw !== form.pw2;

  return (
    <div
      className="admin-scope dark flex min-h-screen items-center justify-center"
      style={{ background: '#07090e' }}
    >
      {/* Ambient violet glow */}
      <div
        className="pointer-events-none fixed inset-0"
        style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(130,98,161,0.06) 0%, transparent 70%)' }}
      />

      <div className="relative w-full max-w-sm px-6">
        {/* Logo mark */}
        <div className="mb-8 flex flex-col items-center gap-3">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-2xl"
            style={{ background: '#0d1522', boxShadow: '0 0 0 1px rgba(130,98,161,0.20), 0 0 24px rgba(130,98,161,0.08)' }}
          >
            <span className="font-mono text-lg font-bold" style={{ color: '#8262A1' }}>B</span>
          </div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-600">BOROO LIQUIDS · STORE</p>
        </div>

        {/* Card */}
        <div
          className="overflow-hidden rounded-2xl"
          style={{ background: '#0d1522', boxShadow: '0 0 0 1px rgba(255,255,255,0.07), 0 32px 64px rgba(0,0,0,0.5)' }}
        >
          <div className="px-7 py-7">
            <h1 className="text-xl font-semibold text-slate-100">매장 관리자 회원가입</h1>
            <p className="mt-1 text-sm text-slate-500">매장 코드로 가입 요청</p>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500" htmlFor="signup-store-code">매장 코드</label>
                <input
                  id="signup-store-code"
                  type="text"
                  placeholder="매장 코드"
                  value={form.store_code}
                  onChange={(e) => setForm({ ...form, store_code: e.target.value })}
                  autoComplete="off"
                  autoFocus
                  className="h-10 w-full rounded-lg bg-white/[0.05] px-3 text-sm text-slate-100 outline-none ring-1 ring-white/[0.08] placeholder:text-slate-700 focus:ring-[#8262A1]/50 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500" htmlFor="signup-username">아이디</label>
                <input
                  id="signup-username"
                  type="text"
                  placeholder="아이디"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  autoComplete="username"
                  className="h-10 w-full rounded-lg bg-white/[0.05] px-3 text-sm text-slate-100 outline-none ring-1 ring-white/[0.08] placeholder:text-slate-700 focus:ring-[#8262A1]/50 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500" htmlFor="signup-pw">비밀번호</label>
                <input
                  id="signup-pw"
                  type="password"
                  placeholder="4자 이상"
                  value={form.pw}
                  onChange={(e) => setForm({ ...form, pw: e.target.value })}
                  autoComplete="new-password"
                  className="h-10 w-full rounded-lg bg-white/[0.05] px-3 text-sm text-slate-100 outline-none ring-1 ring-white/[0.08] placeholder:text-slate-700 focus:ring-[#8262A1]/50 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500" htmlFor="signup-pw2">비밀번호 확인</label>
                <input
                  id="signup-pw2"
                  type="password"
                  placeholder="••••••••"
                  value={form.pw2}
                  onChange={(e) => setForm({ ...form, pw2: e.target.value })}
                  autoComplete="new-password"
                  className={cn(
                    'h-10 w-full rounded-lg bg-white/[0.05] px-3 text-sm text-slate-100 outline-none ring-1 placeholder:text-slate-700 transition-all',
                    pwMismatch ? 'ring-rose-500/50' : 'ring-white/[0.08] focus:ring-[#8262A1]/50'
                  )}
                />
              </div>

              {pwMismatch && <p className="text-xs text-rose-400">비밀번호가 서로 달라.</p>}
              {error && <p className="text-xs text-rose-400">{error}</p>}

              <button
                type="submit"
                disabled={!canSubmit || loading}
                className="mt-1 h-10 w-full rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: '#8262A1', color: '#ffffff' }}
              >
                {loading ? '요청 중…' : '가입 요청'}
              </button>
            </form>
          </div>

          <div
            className="flex items-center justify-between px-7 py-4"
            style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
          >
            <Link to="/store-admin/login" className="text-xs text-slate-600 transition-colors hover:text-slate-400">
              이미 계정이 있어? 로그인
            </Link>
            <Link to="/admin/login" className="text-xs text-slate-600 transition-colors hover:text-slate-400">
              상위관리자 로그인
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
